import { Icon } from '@components/icon/Icon';
import { centsToCompactDollar } from '@utils/currency';
import { ISOTimeTo12HourTime } from '@utils/time';
import cx from 'classnames';
import type { ReactElement } from 'react';
import typography from '~styles/typography.scss';
import type { AdminAvailabilityData } from './apiHelpers';
import styles from './ConciergeAdminAvailabilityItem.scss';
import { useConciergeAvailabilityContext } from './ConciergeAvailabilityContext';

export interface ConciergeAdminAvailabilityItemProps {
  availability: AdminAvailabilityData;
  availabilityItemAction: ReactElement;
  isFlaggedAvailabilityItem?: boolean;
}

const BEST_AVAILABLE_LABEL = 'Any available';

export const ConciergeAdminAvailabilityItem = ({
  availability,
  availabilityItemAction,
  isFlaggedAvailabilityItem = false,
}: ConciergeAdminAvailabilityItemProps) => {
  const {
    clearSelectedAvailability,
    selectedAvailability,
    setSelectedAvailability,
    setSelectedFloorPlanId,
  } = useConciergeAvailabilityContext();
  const {
    listing: { iconName, id: listingId, name: listingName },
    purchasePrice,
    time,
    isBestAvailable,
  } = availability;
  // Best available & featured upgrade availabilities appear multiple times on
  // the page so we can no longer only look at listing and time because that
  // will select two items.
  const isSelected =
    selectedAvailability?.listing.id === listingId &&
    selectedAvailability?.time === time &&
    selectedAvailability?.flaggedWhenSelected === isFlaggedAvailabilityItem;

  const handleAvailabilityClick = () => {
    if (selectedAvailability && isSelected) {
      clearSelectedAvailability();
    } else {
      setSelectedAvailability({
        ...availability,
        flaggedWhenSelected: isFlaggedAvailabilityItem,
      });
      setSelectedFloorPlanId(availability.listing.floorPlanId);
    }
  };

  const containerStyles = cx(styles.container, {
    [styles.container]: true,
    [styles.isSelected]: isSelected,
  });

  return (
    <li
      className={containerStyles}
      key={listingId}
      aria-label={isSelected ? 'Selected Availability' : 'Availability'}
    >
      <button
        className={styles.availabilityInfo}
        data-testid={`${listingId}-${time}${isFlaggedAvailabilityItem ? '-flagged' : ''}`}
        onClick={handleAvailabilityClick}
        type="button"
      >
        <Icon name={iconName} />
        <div className={styles.column}>
          <div className={styles.row}>
            <span className={cx(typography.c3_20, styles.time)}>
              {ISOTimeTo12HourTime(time)}
            </span>
          </div>
          <div className={cx(typography.t2, styles.description)}>
            {isFlaggedAvailabilityItem && isBestAvailable ? (
              <span>{BEST_AVAILABLE_LABEL}</span>
            ) : (
              <span>{listingName}</span>
            )}
          </div>
        </div>
        <div
          className={cx(typography.h7m, styles.price)}
          data-testid="buy-or-make-offer"
        >
          {centsToCompactDollar(purchasePrice)}
        </div>
      </button>
      {isSelected && (
        <div className={styles.ctaContainer}>{availabilityItemAction}</div>
      )}
    </li>
  );
};
